import React, { FC, useMemo } from 'react';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGeneralChargesContext } from 'contexts/GeneralChargesContext';
import { HUD_1 } from 'utils/constants';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import DocumentModalCalculation from 'views/orders/new-charges/charges-e/components/document-fees-modal/document-modal-calculation';
import { extractData } from 'views/orders/new-charges/charges-e/components/document-fees-modal/services';
import { SplitPayeePayer } from 'views/orders/new-charges/components/split-payee/split-payee';
import SkeletonCharges from 'views/orders/new-charges/components/skeleton';
import { useTableGeneralCharges } from 'views/orders/charges/components/GeneralChargesTable/hooks';
import { Footer } from 'views/orders/charges/components/Footer';
import { ChargeCardRow } from './ChargeCardRow';
import { TotalsCard } from './TotalsCard';

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1000,
    color: '#fff',
  },
  container: {
    padding: `${theme.spacing(1.5)}`,
  },
  cardsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

const lettersPerMonth = ['G'];

type Props = {
  isLoadingInfo?: boolean;
  orderType: string;
};

const ChargesCards: FC<Props> = ({ isLoadingInfo, orderType }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    accounting,
    chargesLetter: currentLetter,
    accountingFilter,
    hideTax,
    policyIsLoading,
    textPredectiveCharges,
    handleCloseModal,
  } = useGeneralChargesContext();

  const accountingData = useMemo(
    () => accounting,
    [accounting]
  );

  const {
    hanldeUpdateCharge,
    isLoading,
    handleDeleteCharge,
    formatedAccountingData,
    hanldeAddEmptyCharges,
    control,
    handleSubmit,
    isValid,
    handleOpenDeleteDocumentChargeModal,
    modalState,
    handleOpenEditDocumentChargeModal,
    handleOpenSplitAmountsModal,
    handleOpenChangeDisclosurePolicy,
    chargesForm,
  } = useTableGeneralCharges({
    accountingData,
    currentLetter,
    accountingFilter
  });

  const onSubmit = () =>
    handleSubmit((data) => {
      const arrayChargesToUpload = data.charges
        .filter(({ description, disabled }) => (description !== '' && description !== null) && disabled === false)
        .map((charge) => {
          return {
            ...charge,
          };
        })
        .map((charge) => {
          const newCharge = charge as any;
          if (charge.code === "document") {
            delete newCharge.number;
          }
          delete newCharge.kind;
          delete newCharge.payee;
          delete newCharge.amount_override;
          delete newCharge.amount_calculated;
          delete newCharge.payees;
          delete newCharge.payers;
          delete newCharge.taxes;
          delete newCharge.disabled;
          delete newCharge.code;
          delete newCharge.payee_id;
          return newCharge;
        });

      hanldeUpdateCharge(arrayChargesToUpload);
    })();

  const isOrderTypeNotHUD1 = orderType !== HUD_1;

  return (
    <FormProvider {...chargesForm}>
      <Box className={classes.container}>
        <form>
          <Box className={classes.cardsList}>
            {formatedAccountingData.map((charge, index) => {
              return (
                <ChargeCardRow
                  key={`${charge.accounting_id}|${charge.number}`}
                  charge={charge}
                  index={index}
                  currentLetter={currentLetter}
                  formatedAccountingData={formatedAccountingData}
                  lettersPerMonth={lettersPerMonth}
                  textPredectiveCharges={textPredectiveCharges}
                  hideTax={hideTax}
                  isOrderTypeNotHUD1={isOrderTypeNotHUD1}
                  onDeleteCharge={handleOpenDeleteDocumentChargeModal}
                  onEditCharge={handleOpenEditDocumentChargeModal}
                  onEditSplitAmount={handleOpenSplitAmountsModal}
                />
              );
            })}
          </Box>

          {isOrderTypeNotHUD1 && (
            <TotalsCard
              control={control}
              hideTax={hideTax}
              isOrderTypeNotHUD1={isOrderTypeNotHUD1}
            />
          )}
        </form>

        <Footer
          isFormValid={isValid}
          hanldeAddEmptyCharges={hanldeAddEmptyCharges}
          handleOpenChangeDisclosurePolicy={handleOpenChangeDisclosurePolicy}
          onSubmit={onSubmit}
        />

        <Backdrop
          className={classes.backdrop}
          open={isLoading || Boolean(policyIsLoading) || Boolean(isLoadingInfo)}
        >
          <CircularProgress color="secondary" />
        </Backdrop>

        <DeleteConfirmationModal
          isOpen={modalState.modal === 'deleteDocument'}
          title={t('orders:deleteChargeTitle')}
          confirmationMessage={t('orders:deleteChargeConfirmation', {
            description: modalState.modal === 'deleteDocument'
              ? extractData(modalState.charge!.description)?.document
              : ''
          })}
          onClose={handleCloseModal}
          onConfirm={handleDeleteCharge}
          isRemoveLoading={isLoading}
        />

        {currentLetter && ['1200', 'E'].includes(currentLetter) &&
          (modalState.modal === 'editDocument' || modalState.modal === 'addDocument') && (
            <DocumentModalCalculation
              open
              documentAccountingId={
                modalState.modal === 'editDocument' ? modalState.charge.accounting_id! : undefined
              }
              documentDescription={
                modalState.modal === 'editDocument' ? modalState.charge.description : undefined
              }
              onClose={() => {
                handleCloseModal();
              }}
              letter={currentLetter === '1200' ? 'L' : currentLetter}
            />
        )}

        {modalState.modal === 'editSplit' && (
          <SplitPayeePayer
            open
            kind={modalState.kind}
            letter={currentLetter}
            charges={modalState.charges}
            onClose={handleCloseModal}
            onSuccess={() => handleCloseModal()}
          />
        )}

        {modalState.modal === 'confirmationModal' && (
          <DeleteConfirmationModal
            isOpen
            title={modalState.confirmationTitle}
            confirmationMessage={modalState.confirmationMessage}
            onConfirm={modalState.onConfirm}
            isRemoveLoading={modalState.isLoading}
            onClose={handleCloseModal}
            buttonConfirmText={t('orders:confirmButton')}
          />
        )}
      </Box>
    </FormProvider>
  );
};

interface CardChargesListProps {
  orderType: string;
}

export const CardChargesList: FC<CardChargesListProps> = ({ orderType }) => {
  const { isFetching, accounting } = useGeneralChargesContext();

  if (isFetching && accounting.length === 0) return <SkeletonCharges />;

  return <ChargesCards orderType={orderType} />;
};
